import { useMemo } from 'react';
import { qsParse } from 'helpers/queryString';
import { useLocation } from 'react-router';

export const DEFAULT_FILTER = {
  sortBy: 'createdAt',
  sortDirection: 'desc',
  visibility: 'any',
  source: null,
  status: null,
  expiration: null,
  featured: null,
  expirationWithin: null,
  locationIds: [],
  sourcingCampaignStatus: [],
};

export const useJobsFilters = ({ forCompany }) => {
  const location = useLocation();

  const defaultFilter = useMemo(() => {
    const baseFilter = { ...DEFAULT_FILTER };

    if (forCompany?.id) {
      return {
        ...baseFilter,
        companyId: forCompany.id.toString(),
      };
    }

    return baseFilter;
  }, [forCompany]);

  const jobsFilters = useMemo(() => {
    const filter = qsParse(location);
    delete filter.page;
    const { locations, companyId, ...otherFilters } = filter;
    // When forCompany is provided, always use its companyId and ignore URL param
    // This prevents showing jobs from other companies on company profile pages
    const currentFilters = { ...defaultFilter, ...otherFilters };
    // Ensure companyId from forCompany takes precedence if it exists
    if (forCompany?.id) {
      currentFilters.companyId = forCompany.id.toString();
    } else if (companyId) {
      // Only use URL companyId if forCompany is not provided
      currentFilters.companyId = companyId;
    }

    if (locations) {
      try {
        const parsedLocations = JSON.parse(atob(locations));
        currentFilters.locations = parsedLocations.filter((item) => item.value !== 'remote');
        currentFilters.remote = currentFilters.locations.length === parsedLocations.length ? '' : 'remote';
        currentFilters.locationIds = parsedLocations.map(({ value }) => value);
      } catch (error) {
        currentFilters.locations = [];
        currentFilters.remote = '';
        currentFilters.locationIds = [];
      }
    }

    const mapNumberOfApplicants = {
      any: 'any',
      1: '1',
      0: '0',
    };
    currentFilters.numberOfApplicants = mapNumberOfApplicants[currentFilters.numberOfApplicants];

    // Parse sourcingCampaignStatus as array (bracket notation in URL: sourcingCampaignStatus[]=value1&sourcingCampaignStatus[]=value2)
    if (currentFilters.sourcingCampaignStatus) {
      if (!Array.isArray(currentFilters.sourcingCampaignStatus)) {
        // Handle single value or string
        currentFilters.sourcingCampaignStatus = [currentFilters.sourcingCampaignStatus].filter(Boolean);
      }
    } else {
      currentFilters.sourcingCampaignStatus = [];
    }

    return currentFilters;
  }, [defaultFilter, location, forCompany]);

  return { jobsFilters, defaultFilter };
};
